import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useFeedbackGate } from "@/components/FeedbackGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Pencil, Eye, Trash2, Lock, FolderOpen, Download, Printer, Search, X, MoreVertical } from "lucide-react";

const PAGE_SIZE = 10;
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const CONVENIOS = { comercio: "Comercio", otro_convenio: "Otro convenio", fuera_convenio: "Fuera de convenio" };
const periodoSueldo = (x) => `${MESES[x.mes - 1]} ${x.anio}${x.quincena === "1" ? " — 1ª quinc." : x.quincena === "2" ? " — 2ª quinc." : ""}`;
const fdate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const norm = (s) => (s == null ? "" : String(s)).toLowerCase();
const matches = (q, ...fields) => fields.some((f) => norm(f).includes(q));

const Pager = ({ page, total, setPage, testid }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs text-slate-600" data-testid={testid}>
      <span data-testid={`${testid}-range`}>Mostrando {from}–{to} de {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Anterior</Button>
        <span data-testid={`${testid}-indicator`}>Página {page} de {pages}</span>
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Siguiente</Button>
      </div>
    </div>
  );
};

const TipoBadge = ({ tipo }) => (
  <span className={`ml-2 inline-block text-[10px] px-1.5 py-0.5 border align-middle ${tipo === "sueldo" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
    {tipo === "sueldo" ? "Sueldo" : "Aguinaldo"}
  </span>
);

const EstadoBadge = ({ emitido, testid }) => emitido
  ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={testid}><Lock size={11} /> Recibo emitido</span>
  : <span className="text-xs text-muted-foreground">Borrador</span>;

export default function ListaPage() {
  const nav = useNavigate();
  const { guard } = useFeedbackGate();
  const [q, setQ] = useState("");
  const [agItems, setAgItems] = useState([]);
  const [suItems, setSuItems] = useState(null);
  const [page, setPage] = useState(1);

  const loadAg = () => api.get("/calculations").then((r) => setAgItems(r.data)).catch(() => {});
  const loadSu = () => api.get("/sueldos/liquidaciones").then((r) => setSuItems(r.data)).catch(() => setSuItems([]));
  useEffect(() => { loadAg(); loadSu(); }, []);

  const rows = useMemo(() => {
    const ag = agItems.map((it) => ({
      tipo: "aguinaldo", key: `ag-${it.id}`, id: it.id,
      legajoNumero: it.legajoNumero, nombre: it.apellidoNombre, cuil: it.cuil,
      convenio: it.convenio, periodo: it.periodo, neto: it.neto,
      fecha: it.fecha, emitido: !!it.locked,
    }));
    const su = (suItems || []).map((x) => ({
      tipo: "sueldo", key: `su-${x.legajoId}`, legajoId: x.legajoId,
      legajoNumero: x.legajoNumero, nombre: x.legajoNombre, cuil: x.cuil,
      convenio: x.convenio, periodo: periodoSueldo(x), neto: x.neto,
      fecha: x.updatedAt, emitido: !!x.reciboGenerado,
    }));
    return [...ag, ...su].sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
  }, [agItems, suItems]);

  const query = norm(q).trim();
  const filtered = useMemo(() => (query ? rows.filter((r) => matches(query, r.legajoNumero, r.nombre, r.cuil)) : rows), [rows, query]);
  useEffect(() => { setPage(1); }, [query]);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const removeAg = async (id) => {
    try { await api.delete(`/calculations/${id}`); toast.success("Cálculo eliminado"); loadAg(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeSu = async (legajoId) => {
    try { await api.delete(`/sueldos/liquidaciones/${legajoId}`); toast.success("Liquidación eliminada"); loadSu(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fetchPdf = async (legajoId, action) => {
    const { data } = await api.get(`/sueldos/liquidaciones/${legajoId}/pdf?action=${action}`, { responseType: "blob" });
    return URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  };
  const download = async (id) => {
    try {
      await guard(async () => {
        const url = await fetchPdf(id, "downloaded");
        const a = document.createElement("a");
        a.href = url; a.download = `recibo_sueldo_${id}.pdf`; a.click();
        toast.success("Recibo descargado");
      });
    } catch { toast.error("Error al descargar"); }
  };
  const print = async (id) => {
    try {
      await guard(async () => {
        const url = await fetchPdf(id, "printed");
        const w = window.open(url, "_blank");
        if (w) w.addEventListener("load", () => w.print());
        toast.success("Enviado a impresión");
      });
    } catch { toast.error("Error al imprimir"); }
  };

  return (
    <AppLayout title="Lista" subtitle="Cálculos guardados de Aguinaldo y Sueldos">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white border border-border p-4">
          <div className="relative max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input type="text" placeholder="Buscar por N° de legajo, nombre o CUIL…" value={q} onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-8 h-9" data-testid="lista-search" />
            {q && (
              <button type="button" onClick={() => setQ("")} data-testid="lista-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>
            )}
          </div>
        </div>

        <div className="bg-white border border-border" data-testid="lista-unificada">
          <div className="px-5 py-3 brand-bg"><span className="label-tiny label-on-dark">Cálculos guardados</span></div>
          {suItems === null ? (
            <p className="p-8 text-sm text-muted-foreground">Cargando…</p>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lista-empty">
              {query ? <>Sin resultados para «{q}».</> : <>No hay cálculos guardados todavía. Generá uno desde las calculadoras de Aguinaldo o Recibo de Sueldos.</>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lista-table">
                <thead><tr className="border-b border-border text-left">
                  {["SNo.", "Legajo N°", "Apellido y Nombre", "CUIL", "Convenio", "Período", "Neto a cobrar", "Calculado", "Estado", ""].map((h) => (
                    <th key={h} className="p-3 label-tiny">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pageRows.map((r, idx) => (
                    <tr key={r.key} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={r.tipo === "aguinaldo" ? `lista-row-${idx}` : `liq-row-${r.legajoId}`}>
                      <td className="p-3 tabular">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="p-3 font-mono text-xs">{r.legajoNumero || "—"}</td>
                      <td className="p-3 font-medium text-slate-900">{r.nombre || "—"}</td>
                      <td className="p-3 font-mono text-xs">{r.cuil || "—"}</td>
                      <td className="p-3">{CONVENIOS[r.convenio] || r.convenio || "—"}</td>
                      <td className="p-3 whitespace-nowrap">{r.periodo || "—"}<TipoBadge tipo={r.tipo} /></td>
                      <td className="p-3 tabular font-medium">{r.neto != null ? money(r.neto) : "—"}</td>
                      <td className="p-3 tabular">{fdate(r.fecha)}</td>
                      <td className="p-3"><EstadoBadge emitido={r.emitido} testid={`lista-locked-${idx}`} /></td>
                      <td className="p-3">
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`lista-menu-${idx}`} className="rounded-none h-8 w-8"><MoreVertical size={15} /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-none min-w-[230px]">
                              {r.tipo === "aguinaldo" ? (<>
                                {!r.emitido && (
                                  <DropdownMenuItem data-testid={`lista-edit-${idx}`} onClick={() => nav(`/app/calculadora/${r.id}`)}>
                                    <Pencil size={14} className="mr-2" /> Editar
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem data-testid={`lista-view-${idx}`} onClick={() => nav(`/app/calculadora/${r.id}?view=1`)}>
                                  <Eye size={14} className="mr-2" /> Ver
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`lista-del-${idx}`} disabled={r.emitido} onClick={() => removeAg(r.id)} className="text-destructive focus:text-destructive">
                                  <Trash2 size={14} className="mr-2" /> Eliminar{r.emitido ? " (eliminá el recibo primero)" : ""}
                                </DropdownMenuItem>
                              </>) : (<>
                                <DropdownMenuItem data-testid={`liq-ver-${r.legajoId}`} onClick={() => nav(`/app/sueldos?legajo=${r.legajoId}&cargar=1`)}>
                                  <FolderOpen size={14} className="mr-2" /> Ver / Cargar
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`liq-download-${r.legajoId}`} disabled={!r.emitido} onClick={() => download(r.legajoId)}>
                                  <Download size={14} className="mr-2" /> Descargar recibo{!r.emitido ? " (generá el recibo primero)" : ""}
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`liq-print-${r.legajoId}`} disabled={!r.emitido} onClick={() => print(r.legajoId)}>
                                  <Printer size={14} className="mr-2" /> Imprimir recibo{!r.emitido ? " (generá el recibo primero)" : ""}
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`liq-del-${r.legajoId}`} disabled={r.emitido} onClick={() => removeSu(r.legajoId)} className="text-destructive focus:text-destructive">
                                  <Trash2 size={14} className="mr-2" /> Eliminar{r.emitido ? " (eliminá el recibo primero)" : ""}
                                </DropdownMenuItem>
                              </>)}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} total={filtered.length} setPage={setPage} testid="lista-pagination" />
          <p className="text-xs text-muted-foreground px-5 pb-4 pt-3">Sueldos: se conserva únicamente la última liquidación de cada legajo; al guardar una nueva, reemplaza la anterior. Para habilitar el PDF, tocá <b>Generar Recibo</b> en la calculadora correspondiente.</p>
        </div>
      </div>
    </AppLayout>
  );
}
